import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useAttachmentData } from '@databases/attachments';
import { AsyncOperationStatus } from '@databases/redux-types';
import { type DatabaseAttachment } from '@databases/types/database';
import { encodeCssUrl } from '@databases/utils/utils';
import React, { type ReactElement, useMemo } from 'react';
import styled from 'styled-components';

export const COVER_IMAGE_RATIO = [135, 90];

const CoverImageContainer = styled.div<{ background: string }>`
  display: inline-block;
  background: ${(props) => props.background} center center/cover no-repeat;
  border-radius: ${token('radius.xsmall', '2px')};
  position: relative;

  svg {
    height: 100%;
  }

  > :not(svg) {
    // Image icon
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${colors.N200};
  }
`;

export interface CoverImageProps {
  contentId: string | undefined;

  image:
    | Blob
    | DatabaseAttachment
    | File
    | { src: string }
    | { fileId: string }
    | ReactElement
    | string
    | undefined;
  fallback?: Blob | DatabaseAttachment | File | ReactElement | string | undefined;
  className?: string;

  /**
   * The height in pixels or in another unit that the cover image should have. If no height and no width are specified,
   * this defaults to 90px. If no height but a width is specified, it defaults to 90/135 of the width.
   */
  height?: number | string;

  /**
   * The width in pixels or in another unit that the cover image should have. If no width is specified, it defaults to
   * 1.5x the height.
   */
  width?: number | string;
  /**
   * testId associated with cover image
   */
  testId?: string;
}

/**
 * Renders a database cover image. If the cover image is of an existing database, it is loaded from Confluence.
 * If the cover image is a File object, a temporary data URL is created to render it. If the cover image is
 * empty, a gray placeholder with an image icon is rendered.
 * The cover image is rendered with an aspect ratio of 3:2, unless both a width and a height are specified.
 */
const CoverImage: React.FunctionComponent<CoverImageProps> = ({
  contentId,
  image,
  fallback,
  className,
  height,
  width,
  testId,
}) => {
  const imageUrl = useAttachmentData(
    React.isValidElement<any>(image)
      ? undefined
      : typeof image === 'string'
      ? { src: image }
      : image,
    contentId
  );

  let background;
  if (imageUrl.status === AsyncOperationStatus.SUCCEEDED) {
    background = `url(${encodeCssUrl(imageUrl.data)})`;
  } else if (React.isValidElement(image)) {
    background = '#e7e9ee';
  }

  const normalizedHeight =
    width == null && height == null
      ? '90px'
      : typeof height === 'number'
      ? `${height}px`
      : height;
  const normalizedWidth = typeof width === 'number' ? `${width}px` : width;

  const containerStyle = useMemo(
    () => ({
      height: normalizedHeight,
      width: normalizedWidth,
    }),
    [normalizedHeight, normalizedWidth]
  );

  const aspectKeeperStyle = useMemo(
    () => ({
      height: normalizedHeight == null ? undefined : '100%',
      width: normalizedWidth == null ? undefined : '100%',
    }),
    [normalizedHeight, normalizedWidth]
  );

  if (!background) {
    return fallback ? (
      <CoverImage
        contentId={contentId}
        image={fallback}
        className={className}
        height={height}
        width={width}
        testId="cover-image-fallback"
      />
    ) : null;
  } else {
    return (
      <CoverImageContainer
        background={background}
        className={className}
        style={containerStyle}
        data-testid={testId ?? 'cover-image'}
      >
        <svg
          viewBox={`0 0 ${COVER_IMAGE_RATIO[0]} ${COVER_IMAGE_RATIO[1]}`}
          style={aspectKeeperStyle}
        ></svg>
        {React.isValidElement(image) && image}
      </CoverImageContainer>
    );
  }
};

export default CoverImage;
